import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import {
  CASHBACK_SPLIT_PERCENTAGE,
  GAS_FEE_PERCENTAGE,
  GEOMETRIC_RATIO,
  // isWinterCupOpen,
  REFERRAL_SPLIT_PERCENTAGE,
  RUSH_FEE_PERCENTAGE,
  TOURNAMENT_FEE_PERCENTAGE,
  USER_FEE_PERCENTAGE,
} from "./constants";

dayjs.extend(duration);

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Generate a CSS clip-path polygon for pixelated rounded corners.
 * Uses calc(100% - Npx) so it works at any size without knowing dimensions.
 *
 * @param step - pixel step size (e.g. 2 = each "pixel" is 2px)
 * @param corners - which corners to round (default: all four)
 * @param inset - shift all edges inward by this many px (for bordered inner layer)
 */
export function pixelCornerClipPath(
  step = 2,
  corners: ("tl" | "tr" | "br" | "bl")[] = ["tl", "tr", "br", "bl"],
  inset = 0,
): string {
  const i = inset;
  const s = step;
  const s2 = step * 2;
  const has = (c: "tl" | "tr" | "br" | "bl") => corners.includes(c);

  // Helpers for inset-aware coordinates
  const l = (v: number) => `${v + i}px`; // left edge
  const t = (v: number) => `${v + i}px`; // top edge
  const r = (v: number) => `calc(100% - ${v + i}px)`; // right edge
  const b = (v: number) => `calc(100% - ${v + i}px)`; // bottom edge

  const points: string[] = [];

  // Top-left corner
  if (has("tl")) {
    points.push(
      `${l(0)} ${t(s2)}`,
      `${l(s)} ${t(s2)}`,
      `${l(s)} ${t(s)}`,
      `${l(s2)} ${t(s)}`,
      `${l(s2)} ${t(0)}`,
    );
  } else {
    points.push(`${l(0)} ${t(0)}`);
  }

  // Top-right corner
  if (has("tr")) {
    points.push(
      `${r(s2)} ${t(0)}`,
      `${r(s2)} ${t(s)}`,
      `${r(s)} ${t(s)}`,
      `${r(s)} ${t(s2)}`,
      `${r(0)} ${t(s2)}`,
    );
  } else {
    points.push(`${r(0)} ${t(0)}`);
  }

  // Bottom-right corner
  if (has("br")) {
    points.push(
      `${r(0)} ${b(s2)}`,
      `${r(s)} ${b(s2)}`,
      `${r(s)} ${b(s)}`,
      `${r(s2)} ${b(s)}`,
      `${r(s2)} ${b(0)}`,
    );
  } else {
    points.push(`${r(0)} ${b(0)}`);
  }

  // Bottom-left corner
  if (has("bl")) {
    points.push(
      `${l(s2)} ${b(0)}`,
      `${l(s2)} ${b(s)}`,
      `${l(s)} ${b(s)}`,
      `${l(s)} ${b(s2)}`,
      `${l(0)} ${b(s2)}`,
    );
  } else {
    points.push(`${l(0)} ${b(0)}`);
  }

  return `polygon(${points.join(", ")})`;
}

export const formatAddress = (address: string) => {
  return `${address?.slice(0, 4)}••••${address?.slice(-4)}`;
};

export const formatEntryDisplay = (value: number): string | number =>
  value === 0 ? "FREE" : value;

export function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

type PixelScaleVariable = `--${string}`;

export type PixelScaleConfig = {
  idealViewportWidth?: number;
  minViewportWidth?: number;
  maxViewportWidth?: number;
  variableName?: PixelScaleVariable;
};

const DEFAULT_PIXEL_SCALE_VARIABLE: PixelScaleVariable = "--pixel-scale";

export type ScaleWithPixelRatioOptions = {
  variableName?: PixelScaleVariable;
  unit?: string;
};

// Produces a CSS ratio variable that linearly scales design pixels across viewport widths.
export function createPixelScaleVars({
  idealViewportWidth = 1280,
  minViewportWidth = 320,
  maxViewportWidth = 1920,
  variableName = DEFAULT_PIXEL_SCALE_VARIABLE,
}: PixelScaleConfig = {}) {
  const toPx = (value: number) => `${value}px`;

  return {
    [variableName]: `calc(clamp(${toPx(minViewportWidth)}, 100vw, ${toPx(maxViewportWidth)}) / ${toPx(idealViewportWidth)})`,
  } as Record<PixelScaleVariable, string>;
}

export function scaleWithPixelRatio(
  value: number,
  {
    variableName = DEFAULT_PIXEL_SCALE_VARIABLE,
    unit = "px",
  }: ScaleWithPixelRatioOptions = {},
) {
  return `calc(${value}${unit} * var(${variableName}))`;
}

export const BOT_PNL_DEDUCTION = 2;

export const humanizedDurationsMap = {
  "30": "30s",
  "60": "1m",
  "300": "5m",
  "3600": "1h",
  "14400": "4h",
  "43200": "12h",
  "86400": "1d",
};

export const trophyAmountMap: Record<string, string> = {
  "60": "±7",
  "300": "±30",
  "3600": "±75",
  "14400": "±400",
  "43200": "±600",
  "86400": "±1500",
};

/**
 * Convert seconds to humanized duration format using dayjs
 * @param seconds - Duration in seconds
 * @returns Humanized duration string (e.g., "1m", "5m", "1h", "2d", "1w")
 */
export function humanizeDuration(seconds: number): string {
  const dur = dayjs.duration(seconds, "seconds");

  const days = dur.days();
  const hours = dur.hours();
  const minutes = dur.minutes();

  // If it's more than a week, show weeks
  if (days >= 7) {
    const weeks = Math.floor(days / 7);
    return `${weeks}w`;
  }

  // If it's more than a day, show days
  if (days > 0) {
    return `${days}d`;
  }

  // If it's more than an hour, show hours
  if (hours > 0) {
    return `${hours}h`;
  }

  // Otherwise show minutes
  return `${minutes}m`;
}

export function generateTextShadow(offset: number, color = "#000") {
  return `
    -${offset}px -${offset}px 0 ${color},
    ${offset}px -${offset}px 0 ${color},
    -${offset}px ${offset}px 0 ${color},
    ${offset}px ${offset}px 0 ${color},
    -${offset}px 0px 0 ${color},
    ${offset}px 0px 0 ${color},
    0px ${offset}px 0 ${color},
    0px -${offset}px 0 ${color},

    0 ${offset + 0.5}px 0 ${color},
    ${offset - 0.05}px ${offset + 0.5}px 0 ${color},
    -${offset + 0.05}px ${offset + 0.5}px 0 ${color},

    0 ${offset + 1.5}px 0 ${color},
    ${offset}px ${offset + 1.5}px 0 ${color},
    -${offset}px ${offset + 1.5}px 0 ${color}
    `;
}

export const PLAYER_COLORS_BY_INDEX = [
  // "hsl(82.71,78%,55%)", // lime-400
  // "hsl(351.3,95%,71%)", // rose-400
  // "hsl(141.89,69%,58%)", // green-400
  // "hsl(328.62,86%,70%)", // pink-400
  // "hsl(172.46,66%,50%)", // teal-400
  // "hsl(292.03,91%,73%)", // fuschia-400
  // "hsl(187.94,86%,53%)", // cyan-400
  // "hsl(270,95%,75%)", // purple-400
  // "hsl(198.44,93%,60%)", // sky-400
  // "hsl(27.02,96%,61%)", // orange-400
  // "hsl(211,96%,37%)", //cfl blue
  "hsl(0,0%,100%,0.55)", // white
];

export function formatEpochToReadableDate(epoch: number): string {
  const date = new Date(epoch < 1e12 ? epoch * 1000 : epoch);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "long" });
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  const getOrdinal = (d: number) => {
    if (d > 3 && d < 21) return "th";
    switch (d % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${month} ${day}${getOrdinal(day)}, ${year} ・ ${hours}:${minutes}`;
}

/**
 * Convert an epoch timestamp into a human-readable HH:MM string (24-hour).
 * Accepts seconds or milliseconds.
 */
export function formatEpochToHourMinute(epoch: number | string): string {
  const numericEpoch = Number(epoch);
  if (!Number.isFinite(numericEpoch)) return "--";

  const targetMs = numericEpoch < 1e12 ? numericEpoch * 1000 : numericEpoch;
  const diffMs = targetMs - Date.now();

  if (diffMs <= 0) return "0m";

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours <= 0) {
    return `${minutes}m`;
  }

  return `${hours}h${minutes > 0 ? ` ${minutes}m` : ""}`;
}

// Time of day utilities
export {
  useTimeOfDay,
  getTimePeriod,
  getTimeOfDayInfo,
  isNightTime,
  isMorningTime,
  type TimePeriod,
  type TimeOfDayInfo,
} from "./hooks/useTimeOfDay";

// Time-based assets hook
export { useTimeBasedAssets } from "./hooks/useTimeBasedAssets";

export const formations = {
  "2-2-1": {
    name: "Balanced",
    positions: [
      { id: "pos1", x: 35, y: 80, label: "CB" },
      { id: "pos2", x: 65, y: 80, label: "CB" },
      { id: "pos3", x: 25, y: 40, label: "MF" },
      { id: "pos4", x: 75, y: 40, label: "MF" },
      { id: "pos5", x: 50, y: 20, label: "ST" },
    ],
  },
  "5-0-0": {
    name: "Conservative",
    positions: [
      { id: "pos1", x: 35, y: 70, label: "CB" },
      { id: "pos2", x: 65, y: 70, label: "CB" },
      { id: "pos3", x: 20, y: 25, label: "CB" },
      { id: "pos4", x: 50, y: 20, label: "CB" },
      { id: "pos5", x: 80, y: 25, label: "CB" },
    ],
  },
  "0-5-0": {
    name: "Moderate",
    positions: [
      { id: "pos1", x: 20, y: 80, label: "MF" },
      { id: "pos2", x: 50, y: 80, label: "MF" },
      { id: "pos3", x: 80, y: 80, label: "MF" },
      { id: "pos4", x: 35, y: 35, label: "MF" },
      { id: "pos5", x: 65, y: 35, label: "MF" },
    ],
  },
  "0-0-5": {
    name: "Aggressive",
    positions: [
      { id: "pos1", x: 20, y: 65, label: "ST" },
      { id: "pos2", x: 50, y: 65, label: "ST" },
      { id: "pos3", x: 80, y: 65, label: "ST" },
      { id: "pos4", x: 35, y: 20, label: "ST" },
      { id: "pos5", x: 65, y: 20, label: "ST" },
    ],
  },
};

export type PrizeRow = {
  squads: number;
  rewardUsdc: number;
  rewardFiat?: number | null;
  highlighted: boolean;
  highlightedIndex?: number;
};
export function generatePrizeData(
  entryFee: number,
  joinedPlayers: number,
  solPrice?: number,
): PrizeRow[] {
  const BASE_END = 3;
  const STEP = 1;

  // Extend by +1 for each player beyond the first once more than two have joined
  const extra = joinedPlayers > 2 ? Math.max(0, joinedPlayers - 2) * STEP : 0;
  const end = BASE_END + extra;
  const start = 1;

  // Highlight only if joinedPlayers >= 1
  const highlightSquad = joinedPlayers >= 1 ? joinedPlayers : null;

  const rows: PrizeRow[] = Array.from({ length: end - start + 1 }, (_, i) => {
    const squads = end - i; // descending order
    const totalPool = squads * entryFee;
    const rewardUsdc =
      totalPool * (1 - TOURNAMENT_FEE_PERCENTAGE - GAS_FEE_PERCENTAGE);
    return {
      squads,
      rewardUsdc,
      rewardFiat: rewardUsdc,
      highlighted: highlightSquad !== null && squads <= highlightSquad,
    };
  });

  const hi = rows.findIndex((r) => r.highlighted);
  return hi >= 0 ? rows.map((r) => ({ ...r, highlightedIndex: hi })) : rows;
}

/** USDC icon */
export const USDC_ICON = "https://cdn.navalabs.io/icons/usdc-icon_24GLmN.png";
export const WLD_ICON =
  "https://coin-images.coingecko.com/coins/images/31069/large/worldcoin.jpeg?1696529903";

export const REWARD_ICONS = [
  USDC_ICON,
  "https://cdn.navalabs.io/icons/trophy-icon_zozppj.png",
  "https://cdn.navalabs.io/icons/streak_o2Ar9o.png",
  "https://cdn.navalabs.io/icons/solana-icon_zt7ksz.png",
];

export function formatReadable(num: number): string {
  // Convert to decimal string, handling scientific notation
  const numStr = num.toString();

  // If it's in scientific notation, use toFixed
  if (numStr.includes("e")) {
    const [base, exponent] = numStr.split("e");
    const exp = Number.parseInt(exponent);
    if (exp < 0) {
      const absExp = Math.abs(exp);
      const zeroSubscript = String.fromCharCode(8320 + absExp - 1);
      const baseNum = Number.parseFloat(base);
      const significantDigits = baseNum.toString().replace(".", "").slice(0, 5);
      return `0.0${zeroSubscript}${significantDigits}`;
    }
  }

  const decimalIndex = numStr.indexOf(".");

  // No decimal point or regular number, return with proper precision
  if (decimalIndex === -1) return numStr;
  if (decimalIndex > 1 || (decimalIndex === 1 && numStr[0] !== "0")) {
    return Number.parseFloat(numStr)
      .toPrecision(6)
      .replace(/\.?0+$/, "");
  }

  const decimalPart = numStr.slice(decimalIndex + 1);
  let zeroCount = 0;

  for (let i = 0; i < decimalPart.length; i++) {
    if (decimalPart[i] === "0") zeroCount++;
    else break;
  }

  // No trailing zeros or few zeros, return the number with limited precision
  if (zeroCount <= 4) {
    return Number.parseFloat(numStr)
      .toPrecision(6)
      .replace(/\.?0+$/, "");
  }

  let significantPart = decimalPart.slice(zeroCount);
  significantPart = significantPart.slice(0, 5);
  const zeroSubscript = String.fromCharCode(8320 + zeroCount);

  return `0.0${zeroSubscript}${significantPart}`;
}

// Calculate fee distribution using constants
export function calculateFeeDistribution(
  totalPool: number,
  isGrandRush = false,
) {
  const feePercentage = isGrandRush
    ? RUSH_FEE_PERCENTAGE.grand
    : RUSH_FEE_PERCENTAGE.rush;
  const totalFee = totalPool * feePercentage;
  const userFee = totalFee * USER_FEE_PERCENTAGE;
  const referralAmount = userFee * REFERRAL_SPLIT_PERCENTAGE;
  const cashbackAmount = userFee * CASHBACK_SPLIT_PERCENTAGE;
  const gasFee = totalPool * GAS_FEE_PERCENTAGE;
  const winnersPool = totalPool - totalFee - gasFee;

  return {
    totalFee,
    userFee,
    referralAmount,
    cashbackAmount,
    gasFee,
    winnersPool,
  };
}

// Generate bottom-up payout structure ensuring minimum entry fee for lowest winner
export function generateBottomUpPayout(
  numPlayers: number,
  entryAmountSol: number,
  solPrice: number,
  isGrandRush = false,
  isRookie = false,
) {
  // Normalize incoming player count and ensure we always preview at least 2 players
  const normalizedPlayers = Number.isFinite(numPlayers)
    ? numPlayers
    : Number(numPlayers ?? 0);
  const effectivePlayerCount = Math.max(2, normalizedPlayers);

  // Calculate winners: top floor(n/2) winners
  const numWinners = Math.max(1, Math.floor(effectivePlayerCount / 2));

  // Calculate prize pool distribution using constants
  const totalSolPool = effectivePlayerCount * entryAmountSol;
  const { winnersPool } = calculateFeeDistribution(totalSolPool, isGrandRush);

  // Check if it's Friday for 2x trophy bonus (using UTC)
  // const currentDate = new Date();
  // const isFriday = currentDate.getUTCDay() === 5; // 5 = Friday (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  // const trophyMultiplier = isFriday ? 2 : 1;
  const trophyMultiplier = 1;

  // Calculate trophy pool: joined_players * base (1000 for rush, 5000 for grand-rush)
  // Apply 2x multiplier on Fridays
  const baseTrophiesPerPlayer = isGrandRush ? 5000 : isRookie ? 500 : 1000;
  const totalTrophyPool =
    effectivePlayerCount * baseTrophiesPerPlayer * trophyMultiplier;

  // Bottom-up approach: start with minimum entry fee for lowest winner
  const minimumReward = entryAmountSol;

  // Calculate geometric weights with static ratio
  const weights: number[] = [];
  for (let i = 0; i < numWinners; i++) {
    weights.push(Math.pow(GEOMETRIC_RATIO, i));
  }
  const weightSum = weights.reduce((a, b) => a + b, 0);

  // Start with minimum rewards and scale up for USD
  const baseSolRewards: number[] = [];
  let totalBaseSolRewards = 0;

  for (let i = 0; i < numWinners; i++) {
    const share = weights[i] / weightSum;
    const baseReward = Math.max(minimumReward, winnersPool * share);
    baseSolRewards.push(baseReward);
    totalBaseSolRewards += baseReward;
  }

  // If total base rewards exceed winners pool, scale down proportionally
  let finalSolRewards: number[];
  if (totalBaseSolRewards > winnersPool) {
    const scaleFactor = winnersPool / totalBaseSolRewards;
    finalSolRewards = baseSolRewards.map((reward) => reward * scaleFactor);
  } else {
    finalSolRewards = baseSolRewards;
  }

  // Calculate trophy rewards (using original logic)
  let allocatedTrophies = 0;
  const trophyRewards: number[] = [];

  for (let i = 0; i < numWinners; i++) {
    const share = weights[i] / weightSum;
    const trophyReward = Math.floor(totalTrophyPool * share);
    trophyRewards.push(trophyReward);
    allocatedTrophies += trophyReward;
  }

  // Add any trophy remainder to first place
  const trophyRemainder = totalTrophyPool - allocatedTrophies;
  if (trophyRemainder > 0 && trophyRewards.length > 0) {
    trophyRewards[0] += trophyRemainder;
  }

  // Generate payout structure
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payout: any[] = [];
  for (let i = 0; i < numWinners; i++) {
    // Determine rank display
    let rank: string;
    if (i === 0) rank = "1st";
    else if (i === 1) rank = "2nd";
    else if (i === 2) rank = "3rd";
    else rank = `${i + 1}th`;

    payout.push({
      rank,
      range: { min: i + 1, max: i + 1 },
      rewards: [
        { amount: finalSolRewards[i].toFixed(2), itemId: 0 }, // USD reward
        // NOTE: +500 for rush, +1000 for grand-rush base trophies rewards
        { amount: trophyRewards[i] + (isGrandRush ? 2500 : isRookie ? 250 : 500), itemId: 1 }, // Trophy reward
      ],
    });
  }

  return {
    payout,
    totalPrize: `$${(totalSolPool * solPrice).toFixed(2)}`,
  };
}

export function generateUserHostedPayout(
  maximumPlayers: number,
  betAmount: number,
  topWinner: number,
  solPrice: number = 0,
): {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payoutList: any;
  totalUsdcReward: number;
} {
  const players = Number(maximumPlayers);
  const totalCollected = players * betAmount;

  // User-hosted: generate bottom-up payout distribution with top_winner winners
  const payoutList = generateBottomUpPayoutWithTopWinner(
    players,
    betAmount,
    topWinner,
    solPrice,
  );
  // Calculate total reward after 15% fee + 0.5% gas fee
  const totalUsdcReward =
    totalCollected * (1 - TOURNAMENT_FEE_PERCENTAGE - GAS_FEE_PERCENTAGE);

  return {
    payoutList,
    totalUsdcReward,
  };
}

// Generate bottom-up payout structure with specified number of winners
export function generateBottomUpPayoutWithTopWinner(
  numPlayers: number,
  entryAmountSol: number,
  topWinner: number,
  solPrice: number,
) {
  const RUSH_BASE_PLAYER_TROPHIES = 1000;
  const BASE_LOSER_TROPHIES = 500;

  // If no one joined, show payout for 1 person joined as preview
  const effectivePlayerCount = Math.max(1, numPlayers);

  // Use top_winner as number of winners, but ensure it doesn't exceed half the players
  const numWinners = Math.max(
    1,
    Math.min(topWinner, Math.floor(effectivePlayerCount / 2)),
  );

  // Calculate prize pool distribution using constants
  const totalSolPool = effectivePlayerCount * entryAmountSol;
  const { winnersPool } = calculateFeeDistribution(totalSolPool);

  // Check if it's Friday for 2x trophy bonus (using UTC)
  const currentDate = new Date();
  const isFriday = currentDate.getUTCDay() === 5; // 5 = Friday (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const trophyMultiplier = isFriday ? 2 : 1;

  // Calculate trophy pool: joined_players * base trophies per player
  // Apply 2x multiplier on Fridays
  const totalTrophyPool =
    effectivePlayerCount * RUSH_BASE_PLAYER_TROPHIES * trophyMultiplier;

  // Fair USD distribution: ensure we distribute exactly the winnersPool (after fees)
  const minimumReward = entryAmountSol; // Everyone gets at least their entry back

  // Calculate geometric weights with static ratio (rank 1 gets highest weight)
  const weights: number[] = [];
  for (let i = 0; i < numWinners; i++) {
    weights.push(Math.pow(GEOMETRIC_RATIO, i));
  }
  const weightSum = weights.reduce((a, b) => a + b, 0);

  // Step 1: Calculate proportional shares based on weights
  const proportionalRewards: number[] = [];
  for (let i = 0; i < numWinners; i++) {
    const share = weights[i] / weightSum;
    proportionalRewards.push(winnersPool * share);
  }

  // Step 2: Apply minimum constraint (everyone gets at least entryAmountSol)
  const baseSolRewards: number[] = [];
  for (let i = 0; i < numWinners; i++) {
    baseSolRewards.push(Math.max(minimumReward, proportionalRewards[i]));
  }

  // Step 3: Calculate total with minimums applied
  const totalWithMinimums = baseSolRewards.reduce((a, b) => a + b, 0);

  // Step 4: Distribute exactly winnersPool
  let finalSolRewards: number[];
  if (totalWithMinimums > winnersPool) {
    // If minimums force us over pool, scale down proportionally
    // But ensure we don't go below minimum for any rank
    const excess = totalWithMinimums - winnersPool;
    const scaleableAmount = baseSolRewards.reduce((sum, reward) => {
      return sum + Math.max(0, reward - minimumReward);
    }, 0);

    if (scaleableAmount > 0) {
      // Scale down only the excess above minimum
      const scaleFactor = Math.max(
        0,
        (scaleableAmount - excess) / scaleableAmount,
      );
      finalSolRewards = baseSolRewards.map((reward) => {
        const excessAboveMin = Math.max(0, reward - minimumReward);
        return minimumReward + excessAboveMin * scaleFactor;
      });
    } else {
      // All rewards are at minimum, distribute equally
      finalSolRewards = baseSolRewards.map(() => winnersPool / numWinners);
    }
  } else if (totalWithMinimums < winnersPool) {
    // If we're under pool, distribute remainder proportionally to higher ranks
    const remainder = winnersPool - totalWithMinimums;
    finalSolRewards = [...baseSolRewards];

    // Distribute remainder based on weights (higher ranks get more)
    const remainderWeights = weights.map((w) => w / weightSum);
    for (let i = 0; i < numWinners; i++) {
      finalSolRewards[i] += remainder * remainderWeights[i];
    }
  } else {
    // Perfect match
    finalSolRewards = baseSolRewards;
  }

  // Step 5: Round to ensure exact distribution (handle floating point precision)
  const finalTotal = finalSolRewards.reduce((a, b) => a + b, 0);
  const roundingDiff = winnersPool - finalTotal;
  if (Math.abs(roundingDiff) > 0.0001) {
    // Add/subtract rounding difference to highest rank
    finalSolRewards[0] += roundingDiff;
  }

  // Calculate trophy rewards (using original logic)
  let allocatedTrophies = 0;
  const trophyRewards: number[] = [];

  for (let i = 0; i < numWinners; i++) {
    const share = weights[i] / weightSum;
    const trophyReward = Math.floor(totalTrophyPool * share);
    trophyRewards.push(trophyReward);
    allocatedTrophies += trophyReward;
  }

  // Add any trophy remainder to first place
  const trophyRemainder = totalTrophyPool - allocatedTrophies;
  if (trophyRemainder > 0 && trophyRewards.length > 0) {
    trophyRewards[0] += trophyRemainder;
  }

  // Generate payout structure
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payout: any[] = [];
  for (let i = 0; i < numWinners; i++) {
    // Determine rank display
    let rank: string;
    if (i === 0) rank = "1st";
    else if (i === 1) rank = "2nd";
    else if (i === 2) rank = "3rd";
    else rank = `${i + 1}th`;

    payout.push({
      rank,
      range: { min: i + 1, max: i + 1 },
      rewards: [
        { amount: finalSolRewards[i].toFixed(2), itemId: 0 }, // USD reward
        { amount: trophyRewards[i] + BASE_LOSER_TROPHIES, itemId: 1 }, // Trophy reward
      ],
    });
  }

  return {
    payout,
    totalPrize: `$${(totalSolPool * solPrice).toFixed(2)}`,
  };
}

/** Hardcoded prize list per rank (1-indexed). */
export const GLOBAL_LEADERBOARD_PRIZE_LIST: string[] = [
  "$150", // TIER 1
  "$75", // TIER 2
  "$50", // TIER 3
  "$25", // TIER 4
  "$25",
  "$25",
  "$25",
  "$25",
  "$25",
  "$25",
  "$20", // TIER 5
  "$20",
  "$20",
  "$20",
  "$20",
  "$20",
  "$20",
  "$20",
  "$20",
  "$20",
  "$20",
  "$20",
  "$20",
  "$20",
  "$20",
  "$10", // TIER 6
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
  "$10",
];

type WMAUrlParams = {
  appId: string;
  path: string;
  toAddress: string;
  toToken: string;
  amountUsd: number;
  sourceAppId: string;
  sourceAppName: string;
  sourceDeeplinkPath: string;
};

export function formatCurrency(value: number): string {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  return `$${(value / 1e3).toFixed(1)}K`;
}

export function formatPnl(pnl: number | null): string {
  if (pnl === null) return "—";
  const sign = pnl >= 0 ? "+" : "";
  return `${sign}${pnl.toFixed(2)}%`;
}

export function buildWorldcoinMiniAppUrl(params: WMAUrlParams) {
  const url = new URL("https://worldcoin.org/mini-app");

  url.searchParams.set("app_id", params.appId);
  url.searchParams.set("path", params.path);
  url.searchParams.set("toAddress", params.toAddress);
  url.searchParams.set("toToken", params.toToken);
  url.searchParams.set("amountUsd", params.amountUsd.toString());
  url.searchParams.set("sourceAppId", params.sourceAppId);
  url.searchParams.set(
    "sourceAppName",
    encodeURIComponent(params.sourceAppName),
  );
  url.searchParams.set("sourceDeeplinkPath", params.sourceDeeplinkPath);

  return url.toString();
}
